import type { SubmitEvent } from 'react'
import { useForm } from 'react-hook-form'
import {
	getGetMeQueryKey,
	useUpdateMe,
} from '@cocoonspace/sdk-js/domains/profile/profile'
import { Button } from '@cocoonspace/ui/components/button'
import { Form } from '@cocoonspace/ui/components/form'
import { FormControlledInput } from '@cocoonspace/ui/components/form-controlled-input'
import { Input } from '@cocoonspace/ui/components/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'
import * as z from 'zod'

import { stripLocaleFromPath } from '~/i18n/routing'
import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { useTracking } from '~/providers/tracking-provider'
import { useUserContext } from '~/providers/user-provider'

export type InfosPersoFormValues = z.infer<typeof schema>

const schema = z.object({
	first_name: z.string().min(1),
	last_name: z.string().min(1),
	phone: z.string().min(1),
})

export const InfosPersoForm = ({ onSuccess }: { onSuccess?: VoidFunction }) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const queryClient = useQueryClient()
	const { t } = useLingui()
	const { track } = useTracking()
	const { userQuery } = useUserContext()
	const updateUser = useUpdateMe()
	const httpErrorMsg = useHttpErrorMsg()

	const form = useForm<InfosPersoFormValues>({
		resolver: zodResolver(schema),
		values: {
			first_name: userQuery.data?.first_name || '',
			last_name: userQuery.data?.last_name || '',
			phone: userQuery.data?.phone || '',
		},
	})

	const onSubmit = async (formValues: InfosPersoFormValues) => {
		const query = updateUser.mutateAsync({ data: formValues }).then(() => {
			track.personalInfosUpdate(pathname)

			queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() })
			onSuccess?.()
		})

		toast.promise(query, {
			error: (err: any) =>
				httpErrorMsg[`api.${err.response?.data?.errors[0].code}`]?.() ||
				t`Une erreur est survenue`,
			loading: t`Modifications en cours...`,
			success: t`Les modifications ont bien été prises en compte.`,
		})
	}

	const handleFormSubmit = (event: SubmitEvent<HTMLFormElement>) => {
		event.stopPropagation()
		form.handleSubmit(onSubmit)(event)
	}

	return (
		<form
			className='flex flex-1 flex-col'
			id='personal-informations-form'
			noValidate
			onSubmit={handleFormSubmit}
		>
			<Form {...form}>
				<FormControlledInput
					control={form.control}
					label={t`Prénom`}
					name='first_name'
					render={({ field }) => <Input {...field} />}
					required
				/>

				<FormControlledInput
					control={form.control}
					label={t`Nom`}
					name='last_name'
					render={({ field }) => <Input {...field} />}
					required
				/>

				<FormControlledInput
					control={form.control}
					label={t`Numéro de téléphone`}
					name='phone'
					render={({ field }) => <Input {...field} />}
					required
				/>

				<Button
					className='mt-4 w-full md:mt-auto'
					data-testid=''
					disabled={updateUser.isPending}
					type='submit'
				>
					{t`Valider`}
				</Button>
			</Form>
		</form>
	)
}
